import { PayloadAction, createSlice, isPending, isRejected } from '@reduxjs/toolkit';
import { IContentBlockInstance, IContentPage } from '@/models/content-page.model';
import { SupportedLocales } from '@/models/localization.model';
import { createEmptyBlock } from '../helpers/blockFactory';
import { removeBlock as removeBlockFromList } from '../helpers/replaceBlock';
import { splitGalleryDataToLocales, splitMergedBlockDataToLocales } from '../helpers/blockInspectorLocaleMerge';
import { mapBlogDetailToContentPage } from '../helpers/blogPostMapper';
import {
  deleteContentPage,
  getBlogCategories,
  getContentBlockSchemas,
  getContentPageById,
  getContentPagesList,
  patchContentPage,
  revertContentPage,
  saveContentPage,
  setContentPagePublished,
} from './actionCreators';
import { BlogPostListSortDirection, BlogPostListSortField } from '@/models/blog-post.model';
import { ContentPagesMetaFieldErrors, ContentPagesState, CONTENT_PAGES_SLICE_NAME, initialState } from './models';

type LocaleMetaPatch = Partial<Pick<IContentPage, 'pageType' | 'title' | 'slug' | 'tags'>>;

export const slice = createSlice({
  name: CONTENT_PAGES_SLICE_NAME,
  initialState,
  reducers: {
    setListPage(state: ContentPagesState, action: PayloadAction<number>) {
      state.list.currentPage = action.payload;
    },
    setListBlogCategoryFilter(state: ContentPagesState, action: PayloadAction<string | null>) {
      state.list.blogCategoryId = action.payload;
      state.list.currentPage = 1;
    },
    setListSearch(state: ContentPagesState, action: PayloadAction<string>) {
      state.list.search = action.payload;
      state.list.currentPage = 1;
    },
    setListSearchId(state: ContentPagesState, action: PayloadAction<string>) {
      state.list.searchId = action.payload;
      state.list.currentPage = 1;
    },
    setListSearchAuthor(state: ContentPagesState, action: PayloadAction<string>) {
      state.list.searchAuthor = action.payload;
      state.list.currentPage = 1;
    },
    setListSort(
      state: ContentPagesState,
      action: PayloadAction<{ field: BlogPostListSortField; direction: BlogPostListSortDirection }>,
    ) {
      state.list.sortField = action.payload.field;
      state.list.sortDirection = action.payload.direction;
      state.list.currentPage = 1;
    },
    setEditorPage(state: ContentPagesState, action: PayloadAction<IContentPage | null>) {
      state.editor.page = action.payload;
      state.editor.isPageLoading = false;
      state.editor.selectedBlockId = action.payload?.blocks[0]?.id ?? null;
      state.editor.metaFieldErrors = null;
      state.autosave.isInitialSyncDone = false;
      state.autosave.lastPatchedPayload = null;
      state.autosave.hasUnsavedChanges = false;
    },
    setEditorPageLoading(state: ContentPagesState, action: PayloadAction<boolean>) {
      state.editor.isPageLoading = action.payload;
    },
    syncPageByLocale(state: ContentPagesState, action: PayloadAction<SupportedLocales>) {
      const page = state.editor.page;
      if (!page?.localeMeta || !page.localeBlocks) return;
      const meta = page.localeMeta[action.payload];
      const blocks = page.localeBlocks[action.payload] ?? [];
      state.editor.page = { ...page, title: meta.name, slug: meta.slug, blocks };
    },
    setSelectedBlockId(state: ContentPagesState, action: PayloadAction<string | null>) {
      state.editor.selectedBlockId = action.payload;
    },
    setMetaFieldErrors(state: ContentPagesState, action: PayloadAction<ContentPagesMetaFieldErrors | null>) {
      state.editor.metaFieldErrors = action.payload;
    },
    patchMeta(
      state: ContentPagesState,
      action: PayloadAction<{ patch: LocaleMetaPatch; activeLocale: SupportedLocales }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeMeta) return;
      const { patch, activeLocale } = action.payload;
      if (patch.pageType !== undefined) page.pageType = patch.pageType;
      if (patch.tags !== undefined) page.tags = patch.tags;
      if (patch.title !== undefined || patch.slug !== undefined) {
        const row = page.localeMeta[activeLocale];
        page.localeMeta[activeLocale] = {
          ...row,
          ...(patch.title !== undefined ? { name: patch.title } : {}),
          ...(patch.slug !== undefined ? { slug: patch.slug } : {}),
        };
        page.title = patch.title !== undefined ? patch.title : page.localeMeta[activeLocale].name;
        page.slug = patch.slug !== undefined ? patch.slug : page.localeMeta[activeLocale].slug;
      }
    },
    setLocaleTitle(
      state: ContentPagesState,
      action: PayloadAction<{ locale: SupportedLocales; value: string; activeLocale: SupportedLocales }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeMeta) return;
      const { locale, value, activeLocale } = action.payload;
      page.localeMeta[locale] = { ...page.localeMeta[locale], name: value };
      if (locale === activeLocale) {
        page.title = value;
        page.slug = page.localeMeta[locale].slug;
      }
      if (state.editor.metaFieldErrors?.invalidNameLocales?.includes(locale)) {
        const next = state.editor.metaFieldErrors.invalidNameLocales.filter((l) => l !== locale);
        state.editor.metaFieldErrors.invalidNameLocales = next.length ? next : undefined;
      }
    },
    setLocaleSlug(
      state: ContentPagesState,
      action: PayloadAction<{ locale: SupportedLocales; value: string; activeLocale: SupportedLocales }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeMeta) return;
      const { locale, value, activeLocale } = action.payload;
      page.localeMeta[locale] = { ...page.localeMeta[locale], slug: value };
      if (locale === activeLocale) page.slug = value;
      if (state.editor.metaFieldErrors?.invalidSlugLocales?.includes(locale)) {
        const next = state.editor.metaFieldErrors.invalidSlugLocales.filter((l) => l !== locale);
        state.editor.metaFieldErrors.invalidSlugLocales = next.length ? next : undefined;
      }
    },
    setLocaleDescription(state: ContentPagesState, action: PayloadAction<{ locale: SupportedLocales; value: string }>) {
      const page = state.editor.page;
      if (!page?.localeMeta) return;
      const { locale, value } = action.payload;
      page.localeMeta[locale] = { ...page.localeMeta[locale], description: value };
    },
    setBlogCategoryId(state: ContentPagesState, action: PayloadAction<string | null>) {
      if (!state.editor.page) return;
      state.editor.page.blogCategoryId = action.payload;
    },
    setCoverImage(state: ContentPagesState, action: PayloadAction<string | null>) {
      if (!state.editor.page) return;
      state.editor.page.coverImage = action.payload;
    },
    setPinned(state: ContentPagesState, action: PayloadAction<boolean>) {
      if (!state.editor.page) return;
      state.editor.page.isPinned = action.payload;
    },
    setCoverUploading(state: ContentPagesState, action: PayloadAction<boolean>) {
      state.aux.coverUploading = action.payload;
    },
    addBlock(
      state: ContentPagesState,
      action: PayloadAction<{ blockType: string; activeLocale: SupportedLocales; locales: SupportedLocales[] }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeBlocks) return;
      const { blockType, activeLocale, locales } = action.payload;
      const schemaItem = state.aux.schemas.find((item) => item.type === blockType);
      if (!schemaItem) return;
      const block = createEmptyBlock(blockType, schemaItem.schema, locales);
      const nextLocaleBlocks = { ...page.localeBlocks };
      if (Array.isArray(block.data)) {
        const perLocale = splitGalleryDataToLocales(block.data, locales);
        locales.forEach((loc) => {
          nextLocaleBlocks[loc] = [
            ...(nextLocaleBlocks[loc] ?? []),
            { id: block.id, type: block.type, data: perLocale[loc] },
          ];
        });
      } else if (block.data && typeof block.data === 'object') {
        const perLocale = splitMergedBlockDataToLocales(block.data as Record<string, unknown>, locales);
        locales.forEach((loc) => {
          nextLocaleBlocks[loc] = [
            ...(nextLocaleBlocks[loc] ?? []),
            { id: block.id, type: block.type, data: perLocale[loc] },
          ];
        });
      }
      page.localeBlocks = nextLocaleBlocks;
      page.blocks = nextLocaleBlocks[activeLocale] ?? [];
      state.editor.selectedBlockId = block.id;
    },
    reorderBlocks(
      state: ContentPagesState,
      action: PayloadAction<{ blocks: IContentBlockInstance[]; locales: SupportedLocales[] }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeBlocks) return;
      const { blocks, locales } = action.payload;
      const orderIds = blocks.map((b) => b.id);
      const nextLocaleBlocks = { ...page.localeBlocks };
      locales.forEach((loc) => {
        const cur = page.localeBlocks![loc] ?? [];
        const byId = new Map(cur.map((b) => [b.id, b]));
        nextLocaleBlocks[loc] = orderIds.map((orderedId) => {
          const found = byId.get(orderedId);
          if (found) return found;
          const tpl = blocks.find((b) => b.id === orderedId)!;
          return { id: tpl.id, type: tpl.type, data: Array.isArray(tpl.data) ? [] : {} } as IContentBlockInstance;
        });
      });
      page.blocks = blocks;
      page.localeBlocks = nextLocaleBlocks;
    },
    removeBlock(
      state: ContentPagesState,
      action: PayloadAction<{ blockId: string; activeLocale: SupportedLocales; locales: SupportedLocales[] }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeBlocks) return;
      const { blockId, activeLocale, locales } = action.payload;
      const nextLocaleBlocks = { ...page.localeBlocks };
      locales.forEach((loc) => {
        nextLocaleBlocks[loc] = removeBlockFromList(page.localeBlocks![loc] ?? [], blockId);
      });
      page.localeBlocks = nextLocaleBlocks;
      page.blocks = nextLocaleBlocks[activeLocale] ?? [];
      if (state.editor.selectedBlockId === blockId) state.editor.selectedBlockId = null;
    },
    applyInspectorBlock(
      state: ContentPagesState,
      action: PayloadAction<{
        next: IContentBlockInstance;
        activeLocale: SupportedLocales;
        locales: SupportedLocales[];
      }>,
    ) {
      const page = state.editor.page;
      if (!page?.localeBlocks || !state.editor.selectedBlockId) return;
      const blockIndex = page.blocks.findIndex((b) => b.id === state.editor.selectedBlockId);
      if (blockIndex < 0) return;
      const merged = action.payload.next.data;
      if (merged === undefined) return;
      const nextLocaleBlocks = { ...page.localeBlocks };
      const canonical = page.blocks;
      const refBlock = canonical[blockIndex];
      if (!refBlock) return;
      const alignAndSetData = (loc: SupportedLocales, data: Record<string, unknown> | unknown[]) => {
        const list = [...(nextLocaleBlocks[loc] ?? [])];
        while (list.length < canonical.length) {
          const c = canonical[list.length];
          list.push({ id: c.id, type: c.type, data: Array.isArray(c.data) ? [] : {} });
        }
        list[blockIndex] = { ...list[blockIndex], id: refBlock.id, type: refBlock.type, data };
        nextLocaleBlocks[loc] = list;
      };
      if (Array.isArray(merged)) {
        const perLocale = splitGalleryDataToLocales(merged, action.payload.locales);
        action.payload.locales.forEach((loc) => alignAndSetData(loc, perLocale[loc]));
      } else if (merged && typeof merged === 'object') {
        const perLocale = splitMergedBlockDataToLocales(merged as Record<string, unknown>, action.payload.locales);
        action.payload.locales.forEach((loc) => alignAndSetData(loc, perLocale[loc]));
      } else {
        return;
      }
      page.localeBlocks = nextLocaleBlocks;
      page.blocks = nextLocaleBlocks[action.payload.activeLocale] ?? [];
    },
    markAutosaveDraft(state: ContentPagesState, action: PayloadAction<boolean>) {
      if (!state.editor.page) return;
      state.editor.page.isDraft = action.payload;
    },
    syncAutosavePayload(state: ContentPagesState, action: PayloadAction<string>) {
      if (!state.autosave.isInitialSyncDone) {
        state.autosave.isInitialSyncDone = true;
        state.autosave.lastPatchedPayload = action.payload;
        state.autosave.hasUnsavedChanges = false;
        return;
      }
      state.autosave.hasUnsavedChanges = state.autosave.lastPatchedPayload !== action.payload;
    },
    clearContentPagesState() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getContentPagesList.fulfilled, (state: ContentPagesState, action) => {
        state.list.items = action.payload.data;
        state.list.total = action.payload.meta.total;
        state.list.pageSize = action.payload.meta.per_page;
        state.list.currentPage = action.payload.meta.current_page;
        state.list.isLoading = false;
      })
      .addCase(deleteContentPage.fulfilled, (state: ContentPagesState) => {
        state.list.isLoading = false;
      })
      .addCase(getContentBlockSchemas.fulfilled, (state: ContentPagesState, action) => {
        state.aux.schemas = action.payload.data;
        state.aux.schemasReady = true;
      })
      .addCase(getContentBlockSchemas.rejected, (state: ContentPagesState) => {
        state.aux.schemas = [];
        state.aux.schemasReady = true;
      })
      .addCase(getBlogCategories.fulfilled, (state: ContentPagesState, action) => {
        state.aux.blogCategories = action.payload.data ?? [];
      })
      .addCase(getBlogCategories.rejected, (state: ContentPagesState) => {
        state.aux.blogCategories = [];
      })
      .addCase(getContentPageById.fulfilled, (state: ContentPagesState, action) => {
        state.editor.page = mapBlogDetailToContentPage(action.payload.data, SupportedLocales.UK);
        state.editor.selectedBlockId = state.editor.page.blocks[0]?.id ?? null;
        state.editor.metaFieldErrors = null;
        state.editor.isPageLoading = false;
      })
      .addCase(revertContentPage.fulfilled, (state: ContentPagesState, action) => {
        state.editor.page = mapBlogDetailToContentPage(action.payload.data, SupportedLocales.UK);
        state.editor.selectedBlockId = state.editor.page.blocks[0]?.id ?? null;
        state.editor.metaFieldErrors = null;
        state.editor.isReverting = false;
      })
      .addCase(saveContentPage.fulfilled, (state: ContentPagesState, action) => {
        if ('created' in action.payload) {
          state.editor.isSaving = false;
          return;
        }
        state.editor.page = mapBlogDetailToContentPage(action.payload.data, SupportedLocales.UK);
        state.editor.selectedBlockId = state.editor.page.blocks[0]?.id ?? null;
        state.editor.isSaving = false;
      })
      .addCase(setContentPagePublished.fulfilled, (state: ContentPagesState, action) => {
        if (!state.editor.page) return;
        state.editor.page.published = action.payload.published;
      })
      .addCase(patchContentPage.pending, (state: ContentPagesState) => {
        state.autosave.isPatching = true;
      })
      .addCase(patchContentPage.fulfilled, (state: ContentPagesState, action) => {
        state.autosave.lastPatchedPayload = action.payload.payloadHash;
        state.autosave.hasUnsavedChanges = false;
        state.autosave.isPatching = false;
        state.autosave.lastSavedAt = new Date().toISOString();
        if (state.editor.page) {
          state.editor.page.isDraft = true;
        }
      })
      .addCase(patchContentPage.rejected, (state: ContentPagesState) => {
        state.autosave.isPatching = false;
      })
      .addMatcher(isPending(getContentPagesList, deleteContentPage), (state: ContentPagesState) => {
        state.list.isLoading = true;
        state.error = null;
      })
      .addMatcher(isPending(getContentPageById), (state: ContentPagesState) => {
        state.editor.isPageLoading = true;
      })
      .addMatcher(isPending(saveContentPage), (state: ContentPagesState) => {
        state.editor.isSaving = true;
      })
      .addMatcher(isPending(revertContentPage), (state: ContentPagesState) => {
        state.editor.isReverting = true;
      })
      .addMatcher(
        isRejected(
          getContentPagesList,
          deleteContentPage,
          getContentPageById,
          revertContentPage,
          saveContentPage,
          setContentPagePublished,
        ),
        (state: ContentPagesState, action) => {
          state.list.isLoading = false;
          state.editor.isPageLoading = false;
          state.editor.isSaving = false;
          state.editor.isReverting = false;
          state.error = action.error;
        },
      );
  },
});

export const {
  setListPage,
  setListBlogCategoryFilter,
  setListSearch,
  setListSearchId,
  setListSearchAuthor,
  setListSort,
  setEditorPage,
  setEditorPageLoading,
  syncPageByLocale,
  setSelectedBlockId,
  setMetaFieldErrors,
  patchMeta,
  setLocaleTitle,
  setLocaleSlug,
  setLocaleDescription,
  setBlogCategoryId,
  setCoverImage,
  setPinned,
  setCoverUploading,
  addBlock,
  reorderBlocks,
  removeBlock,
  applyInspectorBlock,
  syncAutosavePayload,
  markAutosaveDraft,
  clearContentPagesState,
} = slice.actions;

export default slice.reducer;
